import React, { useState } from "react";
import { Input } from "./ui/input";
import { Phone, Mail, ArrowRight, Check } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { trackConsultationRequest, trackPhoneClick } from "../utils/analytics";

const BENEFITS = [
  "A private assessment of your community's security",
  "A custom dedicated-guard plan, designed for your property",
  "Full camera & license-plate-reader walkthrough",
  "No obligation — we only take a limited number of communities",
];

const ConsultationCTA = () => {
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", community: "", message: "", referralSource: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone) return;
    setIsSubmitting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads/consultation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name, email: formData.email, phone: formData.phone,
          community: formData.community, message: formData.message, referralSource: formData.referralSource,
        }),
      });
      if (response.ok) {
        await response.json();
        trackConsultationRequest({
          community: formData.community, referralSource: formData.referralSource,
          name: formData.name, email: formData.email, phone: formData.phone,
        });
        toast({
          title: "Consultation Requested",
          description: "Our team will contact you within 4 hours to schedule your private assessment.",
          duration: 5000,
        });
        setFormData({ name: "", email: "", phone: "", community: "", message: "", referralSource: "" });
      } else {
        throw new Error('Failed to submit consultation request');
      }
    } catch (error) {
      toast({
        title: "Submission Error",
        description: "There was a problem submitting your request. Please call us at (512) 363-6584.",
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputCls = "w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#D32F2F] transition-colors";

  return (
    <section id="consultation" className="bg-[#0A0A0A] py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-start">
        {/* Left */}
        <div data-reveal>
          <div className="inline-flex items-center gap-2 mb-6 text-xs font-semibold uppercase tracking-[0.2em] text-[#D32F2F]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#D32F2F] pulse-dot" /> Request a Consultation
          </div>
          <h2 className="font-head text-4xl sm:text-5xl font-semibold tracking-tighter leading-[1.03] text-white mb-6">
            See if your community qualifies.
          </h2>
          <p className="text-lg text-white/60 leading-relaxed mb-10 max-w-lg">
            A private, no-pressure consultation to design the right dedicated-guard plan for your property.
            We take on a limited number of premium communities.
          </p>

          <ul className="space-y-4 mb-10">
            {BENEFITS.map((b) => (
              <li key={b} className="flex items-start gap-3">
                <span className="w-5 h-5 rounded-full bg-[#D32F2F] flex items-center justify-center shrink-0 mt-0.5">
                  <Check className="w-3.5 h-3.5 text-white" />
                </span>
                <span className="text-white/75">{b}</span>
              </li>
            ))}
          </ul>

          <div className="flex flex-col sm:flex-row gap-4">
            <a href="tel:+15123636584" onClick={() => trackPhoneClick('consultation_cta')}
               className="flex items-center gap-3 rounded-xl border border-white/10 px-5 py-3 hover:border-[#D32F2F] transition-colors">
              <Phone className="w-5 h-5 text-[#D32F2F]" />
              <div>
                <div className="text-white font-bold">(512) 363-6584</div>
                <div className="text-white/40 text-xs">Mon–Fri, 8AM–6PM</div>
              </div>
            </a>
            <div className="flex items-center gap-3 rounded-xl border border-white/10 px-5 py-3">
              <Mail className="w-5 h-5 text-[#D32F2F]" />
              <div>
                <div className="text-white font-bold text-sm">mike@redlinemonitoring.com</div>
                <div className="text-white/40 text-xs">24-hour response</div>
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <div data-reveal data-reveal-delay="1" className="rounded-2xl bg-[#141416] border border-white/10 shadow-premium p-8 lg:p-10">
          <h3 className="font-head text-2xl font-semibold tracking-tight text-white mb-2">Request your consultation</h3>
          <p className="text-white/50 text-sm mb-8">We'll contact you within 4 hours.</p>
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="consultation-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-white/40 mb-2">Name *</label>
                <Input type="text" name="name" value={formData.name} onChange={handleInputChange} className={inputCls} placeholder="Your full name" required data-testid="consultation-name" />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-white/40 mb-2">Phone *</label>
                <Input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} className={inputCls} placeholder="(512) 123-4567" required data-testid="consultation-phone" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-white/40 mb-2">Email *</label>
              <Input type="email" name="email" value={formData.email} onChange={handleInputChange} className={inputCls} placeholder="your@email.com" required data-testid="consultation-email" />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-white/40 mb-2">Community / Property</label>
              <Input type="text" name="community" value={formData.community} onChange={handleInputChange} className={inputCls} placeholder="Name of your HOA or community" data-testid="consultation-community" />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-white/40 mb-2">Security Concerns</label>
              <textarea name="message" value={formData.message} onChange={handleInputChange} rows={4}
                        className={inputCls} placeholder="Tell us about your current security challenges…" />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-white/40 mb-2">How did you hear about us?</label>
              <select name="referralSource" value={formData.referralSource} onChange={handleInputChange} className={inputCls}>
                <option value="" className="bg-[#141416]">Select an option</option>
                <option value="google" className="bg-[#141416]">Google Search</option>
                <option value="social-media" className="bg-[#141416]">Social Media</option>
                <option value="hoa-recommendation" className="bg-[#141416]">HOA Recommendation</option>
                <option value="word-of-mouth" className="bg-[#141416]">Word of Mouth</option>
                <option value="property-management" className="bg-[#141416]">Property Management Company</option>
                <option value="other" className="bg-[#141416]">Other</option>
              </select>
            </div>
            <button type="submit" disabled={isSubmitting} data-testid="consultation-submit"
                    className="w-full inline-flex items-center justify-center gap-3 rounded-full bg-[#D32F2F] hover:bg-[#B71C1C] text-white font-medium py-4 transition-all hover:scale-[1.01] disabled:opacity-60">
              {isSubmitting ? "Submitting…" : "Request a Consultation"}
              {!isSubmitting && <ArrowRight className="w-5 h-5" />}
            </button>
            <p className="text-[11px] text-white/35 text-center leading-relaxed">
              By submitting this form, you agree to be contacted by Redline Monitoring Services Inc. regarding your consultation.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ConsultationCTA;
